import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { t } from '../../i18n';
import { monthName, MONTHS } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { BankAuszug } from './BankAuszug';
import { belegePath, CATEGORY_LABEL, displayNames } from './labels';
import { useBelegePage, useHistoryWindow } from './nav';
import { Loading } from './Pages';
import { Sammelfotos } from './Sammelfotos';
import type { Category, MonthResponse } from './types';
import { clearPending, dismiss, prune, removePending, retry, stage, upload, useUploads, type UploadItem } from './uploads';

// Dateiwahl: PDF, Fotos, E-Rechnung (XML). Im Ordner „Kontoauszug“ zusätzlich Dateien aus dem Online-Banking: CAMT
// (.xml), MT940 (.sta, .mt940, .940, .txt), CSV und ZIP mit solchen Dateien. Geprüft wird am Inhalt auf dem Server.
const ACCEPT = 'application/pdf,image/*,.xml,application/xml,text/xml';
const ACCEPT_KONTOAUSZUG = `${ACCEPT},.sta,.mt940,.940,.txt,text/plain,.csv,text/csv,.zip,application/zip`;

// Ordner eines Monats: Foto machen, Datei wählen, Sammelfotos; Auswahl bestätigen; hochgeladene Belege.
export function FolderPage({ clientId, year, month, category }: { clientId: string; year: number; month: number; category: Category }) {
  const toast = useToast();
  const navigate = useNavigate();
  const res = useApi<MonthResponse>(`/api/app/belege/${clientId}/years/${year}/${month}`, { live: ['belege'], clientId });
  const d = res.data;
  const label = t(CATEGORY_LABEL[category]);
  useBelegePage({ title: label, clientId, clientName: d?.client.name, year, month, category });

  const key = `${clientId}|${year}|${month}|${category}`;
  const url = `/api/app/belege/${clientId}/years/${year}/${month}/${category}`;
  const { pending, items } = useUploads();
  const mine = pending?.key === key ? pending.files : null;
  const belege = useMemo(() => (d ? d.belege.filter((b) => b.category === category) : []), [d, category]);
  const serverIds = useMemo(() => new Set(belege.map((b) => b.id)), [belege]);
  const local = items.filter((i) => i.key === key && !(i.status === 'done' && i.belegId && serverIds.has(i.belegId)));
  const names = displayNames(belege.map((b) => b.name));

  const cam = useRef<HTMLInputElement>(null);
  const pick = useRef<HTMLInputElement>(null);
  const confirmBtn = useRef<HTMLButtonElement>(null);
  const [over, setOver] = useState(false);
  // Sammelfotos haben einen eigenen Verlaufseintrag, damit die Zurück-Geste nicht die Fotos verwirft
  const [serie, setSerie] = useState(false);
  const serieWin = useHistoryWindow('serie');
  const openSerie = () => {
    setSerie(true);
    serieWin.open();
  };
  const closeSerie = () => {
    setSerie(false);
    serieWin.close();
  };

  // fertige Uploads verschwinden, sobald der Beleg in der Liste steht
  useEffect(() => prune(key, serverIds), [key, serverIds]);

  const staged = mine?.length ?? 0;
  const lastStaged = useRef(0);
  useEffect(() => {
    // nach dem Auswählen den Knopf „Hochladen“ zeigen und darauf setzen
    if (staged > lastStaged.current) {
      confirmBtn.current?.scrollIntoView({ block: 'center' });
      confirmBtn.current?.focus({ preventScroll: true });
    }
    lastStaged.current = staged;
  }, [staged]);

  if (d?.future) return <Navigate to={belegePath(clientId, year)} replace />;

  const onFiles = (list: FileList | null) => {
    if (list?.length) stage(key, Array.from(list), toast);
  };
  const where = `${d?.client.name ?? ''} › ${year} › ${monthName(month)} › ${label}`;
  const booked = d?.status?.status === 'gebucht';
  // gezählt wird, was gespeichert ist oder gerade hochlädt; fehlgeschlagene Dateien stehen nur darunter
  const count = belege.length + local.filter((i) => i.status !== 'error').length;
  // geschriebene Rechnungen (vom Rechnungsprogramm abgelegt) stehen unter eigener Überschrift, nicht als „hochgeladen“
  const rechnungen = belege.filter((b) => b.rechnung);
  const eigene = belege.filter((b) => !b.rechnung);
  const hochgeladen = count - rechnungen.length;
  const nameOf = new Map(belege.map((b, i) => [b.id, names[i]]));
  const zeile = (b: (typeof belege)[number]) => (
    <button
      key={b.id}
      type="button"
      className="file"
      onClick={() => navigate({ search: `?beleg=${b.id}` }, { state: { sheet: true } })}
    >
      <span>{nameOf.get(b.id)}</span>
      <span className="ok">
        {b.comments > 0 && (
          <>
            <span aria-label={b.comments === 1 ? t('1 Kommentar') : t('{n} Kommentare', { n: b.comments })}>
              💬 {b.comments}
            </span>{' '}
            ·{' '}
          </>
        )}
        ✓ {t('gespeichert')} <span className="chev">›</span>
      </span>
    </button>
  );

  return (
    <>
      <div className="hello">
        <h2>{label}</h2>
        <p>
          {monthName(month)} {year}
          {d ? ` · ${d.client.name}` : ''}
        </p>
      </div>
      <div className="actions">
        <button type="button" className="act photo" onClick={() => cam.current?.click()}>
          <span className="big" aria-hidden="true">
            ◉
          </span>
          {t('Foto machen')}
          <small>{t('öffnet die Kamera')}</small>
        </button>
        <button
          type="button"
          className={`act pick${over ? ' over' : ''}`}
          onClick={() => pick.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            onFiles(e.dataTransfer.files);
          }}
        >
          <span className="big" aria-hidden="true">
            ↑
          </span>
          {t('Datei wählen')}
          <small>
            {category === 'kontoauszug'
              ? t('PDF, JPG, Datei aus dem Online-Banking (CAMT, MT940, CSV) · oder hierher ziehen')
              : t('PDF, JPG, E-Rechnung (XML) · oder hierher ziehen')}
          </small>
        </button>
        <button type="button" className="act sammel" onClick={openSerie}>
          <span className="big" aria-hidden="true">
            ▣
          </span>
          {t('Sammelfotos')}
          <small>{t('viele Belege nacheinander fotografieren, z. B. 20–30 Stück, am Ende alle zusammen hochladen')}</small>
        </button>
      </div>
      {/* Ein Foto ersetzt den Papierbeleg nur mit eigenem Verfahren fürs ersetzende Scannen (Gesetzes-Prüfung C5) */}
      <p className="paperhint">{t('Papierbelege bitte trotzdem aufbewahren. Das Foto ersetzt das Original nicht.')}</p>
      {category === 'kontoauszug' && (
        <p className="paperhint">
          {t('Rechnungen und Quittungen bitte in „Eingangsbelege“ oder „Ausgangsbelege“ hochladen, hier nur Kontoauszüge.')}
        </p>
      )}
      {category === 'kontoauszug' && <BankAuszug clientId={clientId} year={year} month={month} />}
      <input
        ref={cam}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        data-testid="beleg-cam"
        onChange={(e) => {
          onFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={pick}
        type="file"
        multiple
        accept={category === 'kontoauszug' ? ACCEPT_KONTOAUSZUG : ACCEPT}
        hidden
        data-testid="beleg-pick"
        onChange={(e) => {
          onFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {mine && mine.length > 0 && (
        <div className="card upconfirm">
          <b>{mine.length === 1 ? t('Diese Datei hochladen?') : t('Diese {n} Dateien hochladen?', { n: mine.length })}</b>
          <ul className="upfiles">
            {mine.map((f, i) => (
              <li key={`${f.name}|${f.size}|${f.lastModified}`}>
                <span>📄 {f.name}</span>
                <button
                  type="button"
                  className="linkbtn"
                  aria-label={t('{name} aus der Auswahl nehmen', { name: f.name })}
                  onClick={() => removePending(i)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          {booked && (
            <div className="uphint">{t('Dieser Monat ist schon gebucht. Ihre Kanzlei bekommt den neuen Beleg zur Prüfung.')}</div>
          )}
          <div className="upwhere">
            {mine.length === 1
              ? t('Ablage: {where}. Danach sieht Ihre Kanzlei die Datei.', { where })
              : t('Ablage: {where}. Danach sieht Ihre Kanzlei die Dateien.', { where })}
          </div>
          <div className="bwa-row">
            <button
              ref={confirmBtn}
              type="button"
              className="btn2 dark"
              onClick={() => {
                clearPending();
                upload(key, url, mine, toast);
              }}
            >
              {mine.length === 1 ? t('Hochladen') : t('{n} Dateien hochladen', { n: mine.length })}
            </button>
            <button type="button" className="btn2" onClick={() => pick.current?.click()}>
              {t('Weitere hinzufügen')}
            </button>
            <button
              type="button"
              className="btn2"
              onClick={() => {
                clearPending();
                toast(t('Nicht hochgeladen'));
              }}
            >
              {t('Abbrechen')}
            </button>
          </div>
        </div>
      )}

      {!d ? (
        <Loading error={res.error} />
      ) : (
        <div className="files">
          {(hochgeladen > 0 || local.length > 0 || rechnungen.length === 0) && (
            <>
              <h3>{t('Hochgeladen ({n})', { n: hochgeladen })}</h3>
              {local.map((i) => (
                <LocalRow key={i.id} item={i} onRetry={() => retry(i.id, toast)} />
              ))}
              {eigene.map(zeile)}
              {count === 0 && local.length === 0 && <div className="empty">{t('Noch nichts in diesem Ordner.')}</div>}
            </>
          )}
          {rechnungen.length > 0 && (
            <>
              <h3>{t('Geschriebene Rechnungen ({n})', { n: rechnungen.length })}</h3>
              {rechnungen.map(zeile)}
            </>
          )}
        </div>
      )}

      {serie && (
        <Sammelfotos
          // Dateinamen bleiben Deutsch (die Kanzlei arbeitet mit ihnen), auch wenn die App eine andere Sprache zeigt
          baseName={`${CATEGORY_LABEL[category]}_${MONTHS[month - 1]}`}
          offset={count}
          inHistory={serieWin.isOpen}
          onBack={serieWin.open}
          onClose={closeSerie}
          onDone={(files) => {
            closeSerie();
            upload(key, url, files, toast);
            toast(files.length === 1 ? t('1 Foto wird hochgeladen') : t('{n} Fotos werden hochgeladen', { n: files.length }));
          }}
        />
      )}
    </>
  );
}

function LocalRow({ item, onRetry }: { item: UploadItem; onRetry(): void }) {
  if (item.status === 'error') {
    return (
      <div className="file uperr" role="group" aria-label={item.file.name}>
        <span className="upn">
          {item.file.name}
          <small>{item.error}</small>
        </span>
        <span className="upb">
          {item.retry && (
            <button type="button" className="btn2" onClick={onRetry}>
              {t('Erneut versuchen')}
            </button>
          )}
          <button type="button" className="btn2" onClick={() => dismiss(item.id)}>
            {t('Entfernen')}
          </button>
        </span>
      </div>
    );
  }
  return (
    <div className="file local">
      <span>{item.file.name}</span>
      {item.status === 'done' ? <span className="ok">✓ {t('gespeichert')}</span> : <span className="wait">↑ {t('lädt…')}</span>}
    </div>
  );
}
